import { OTLayout } from "@/components/layout/ot-layout";
import { Star, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

const CORE_DOMAINS = [
  {
    title: "Expert Clinical Practice",
    description: "Consultant OTs must demonstrate clinical expertise significantly beyond that of other senior practitioners, often holding an advanced or extended scope of practice.",
    examples: [
      "First-contact practitioner or triage role in rheumatology",
      "Prescribing authority (independent or supplementary prescriber)",
      "Requesting imaging or other investigations",
      "Managing complex cases that other clinicians refer to you",
      "Expert clinical opinion sought at regional or national level",
    ],
  },
  {
    title: "Professional Leadership",
    description: "Strategic leadership of occupational therapy in rheumatology, influencing how the service is designed, delivered, and developed across a trust, system, or region.",
    examples: [
      "Clinical lead for rheumatology OT across a trust or ICS",
      "Member of rheumatology MDT leadership or governance committees",
      "Workforce planning and service redesign leadership",
      "Advocating for OT in national rheumatology strategy (BSR, NICE)",
      "Contributing to RCOT SS Rheumatology at committee or national level",
    ],
  },
  {
    title: "Education, Training & Supervision",
    description: "Leading and facilitating education and professional development across OT and the wider MDT, including postgraduate training and clinical academic supervision.",
    examples: [
      "Educational lead for rheumatology OT across a training programme",
      "Supervising postgraduate OT students (MSc, PgDip, PhD)",
      "Delivering national CPD events or conference presentations",
      "Creating and sharing educational resources across the profession",
      "University Honorary appointment and OT student placements",
    ],
  },
  {
    title: "Research & Evidence",
    description: "Leading or participating in research, audit, and quality improvement that generates or applies evidence to improve occupational therapy outcomes in rheumatology.",
    examples: [
      "Principal investigator or co-applicant on funded research projects",
      "Publication in peer-reviewed OT or rheumatology journals",
      "Contributing to systematic reviews or national guideline development",
      "Leading service evaluations with measurable outcome data",
      "Presenting research at BSR, RCOT Annual Conference, or EULAR",
    ],
  },
];

const DEVELOPMENT_STEPS = [
  { step: "1", title: "Establish your case at Band 7", detail: "Most Consultant OT posts require a track record at Band 7 (or equivalent) with evidence of national-level clinical contribution, leadership, and research or education activity." },
  { step: "2", title: "Develop advanced/extended scope practice", detail: "Identify opportunities to extend your clinical scope: independent prescribing, first-contact physiotherapy-style triaging, or other ACP competencies relevant to rheumatology OT." },
  { step: "3", title: "Build your national profile", detail: "Contribute to RCOT SS Rheumatology, BSR OT SIG, or NICE guideline consultations. Present at conferences, publish in OTnews or peer-reviewed journals, and take on national roles." },
  { step: "4", title: "Pursue postgraduate qualifications", detail: "An MSc or equivalent (e.g. in Advanced Practice, Health Research, or Leadership) strengthens Consultant OT applications. Clinical academic programmes (e.g. NIHR internship) are also relevant." },
  { step: "5", title: "Apply for Consultant OT posts", detail: "Consultant OT posts in rheumatology are rare — most are created in response to an individual's developing capabilities. Work with your manager to make the case for a Consultant post, supported by job evaluation under Agenda for Change." },
  { step: "6", title: "Prepare for FRCOT", detail: "Once established in a Consultant OT role, compile evidence for RCOT Fellowship (FRCOT). Fellowship is the natural next step for OTs making national contributions across clinical practice, leadership, education, or research." },
];

export default function OtConsultant() {
  const [openDomain, setOpenDomain] = useState<number | null>(null);
  const [openStep, setOpenStep] = useState<number | null>(null);

  return (
    <OTLayout title="Consultant OT Pathway">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-orange-700 to-amber-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Star className="h-7 w-7 text-orange-300" />
            <div>
              <h2 className="text-xl font-bold">Consultant OT — Rheumatology</h2>
              <p className="text-orange-200 text-sm">Band 8b–8c · The pinnacle of NHS clinical OT practice</p>
            </div>
          </div>
          <p className="text-orange-100 text-sm leading-relaxed">
            The Consultant OT role represents the highest level of clinical occupational therapy practice in the NHS. In rheumatology, Consultant OTs combine expert clinical expertise with strategic leadership, education, and research. This page outlines the four core domains, how to develop your case, and what Consultant OT looks like in rheumatology practice.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-3">What is a Consultant OT?</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            Consultant OT is an NHS role (typically Band 8b or 8c) that combines four core domains of practice: Expert Clinical Practice, Professional Leadership, Education & Training, and Research & Evidence. The role was established as part of NHS advanced practice frameworks to retain expert clinicians in practice while extending their strategic contribution.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            In rheumatology, Consultant OTs may work as first-contact practitioners, independent prescribers, and clinical leads — combining direct expert patient care with workforce development, service transformation, and national influence.
          </p>
          <a
            href="https://www.hee.nhs.uk/our-work/advanced-clinical-practice"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 text-sm text-amber-700 hover:text-amber-800 font-medium"
          >
            <ExternalLink className="h-4 w-4" />
            NHS Advanced Clinical Practice Framework (HEE)
          </a>
        </div>

        {/* Four Core Domains */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <h3 className="font-bold text-slate-900">Four Core Domains of Consultant OT Practice</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {CORE_DOMAINS.map((d, i) => (
              <div key={i}>
                <button
                  onClick={() => setOpenDomain(openDomain === i ? null : i)}
                  className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <p className="text-sm font-semibold text-slate-800">{d.title}</p>
                  {openDomain === i ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                </button>
                {openDomain === i && (
                  <div className="px-6 pb-5 space-y-3">
                    <p className="text-sm text-slate-600 leading-relaxed">{d.description}</p>
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-2">Rheumatology OT examples:</p>
                      <ul className="space-y-1.5">
                        {d.examples.map((e, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-orange-400 shrink-0" />
                            {e}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Development Pathway */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Development Pathway to Consultant OT</h3>
          <div className="space-y-3">
            {DEVELOPMENT_STEPS.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenStep(openStep === i ? null : i)}
                  className="flex items-center gap-4 w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="h-7 w-7 bg-orange-700 text-white rounded-full text-xs font-bold flex items-center justify-center shrink-0">{s.step}</span>
                  <span className="text-sm font-medium text-slate-800 flex-1">{s.title}</span>
                  {openStep === i ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                </button>
                {openStep === i && (
                  <div className="px-4 pb-4 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                    {s.detail}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link href="/ot/cpd/advanced-practice">
              <button className="px-4 py-2 bg-orange-700 hover:bg-orange-800 text-white rounded-xl text-sm font-medium transition-colors">
                Open Advanced Practice Portfolio
              </button>
            </Link>
            <Link href="/ot/career/frcot">
              <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-colors">
                FRCOT Fellowship Guide
              </button>
            </Link>
          </div>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Note:</strong> Consultant OT posts in rheumatology are relatively rare and are typically created in response to a specific individual's developing capabilities and service needs. The pathway above describes a typical development trajectory — your own route will depend on your trust's structure and your specific area of specialisation.
        </div>
      </div>
    </OTLayout>
  );
}
